import { useState, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { 
  ZoneGeometry, 
  ZoneInput, 
  ZoneType,
  useZoneQuery, 
  useUpdateZoneMutation 
} from "@/api/v0/zones";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { ZoneDraw } from "@/components/map/zone-draw";
import { ZoneForm } from "@/components/zones/zone-form";
import { toast } from "sonner";
import { Map } from "react-map-gl/maplibre";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export function ZoneEditRoute() {
  const navigate = useNavigate();
  const params = useParams({ from: "/zones/$zoneId/edit" });
  const zoneId = params.zoneId;
  
  const [zoneGeometry, setZoneGeometry] = useState<ZoneGeometry | null>(null);
  const [step, setStep] = useState<"loading" | "draw" | "details">("loading");
  const [zoneType, setZoneType] = useState<ZoneType | null>(null);
  
  const { data: zone, isLoading, isError } = useZoneQuery(zoneId);
  const updateZoneMutation = useUpdateZoneMutation();

  useEffect(() => {
    if (zone) {
      setZoneGeometry(zone.geometry);
      setStep("draw");
    }
  }, [zone]);

  const handleZoneDrawn = (geometry: ZoneGeometry, type: ZoneType) => {
    setZoneGeometry(geometry);
    setZoneType(type);
  };

  const handleContinue = () => {
    if (!zoneGeometry) {
      toast.error("Please draw a valid zone");
      return;
    }
    
    setStep("details");
  };

  const handleSubmit = async (data: ZoneInput) => {
    if (!zoneId || !zoneGeometry) return;
    
    try {
      await updateZoneMutation.mutateAsync({ 
        zoneId, 
        zoneUpdateInput: {
          ...data,
          geometry: zoneGeometry
        } 
      });
      toast.success("Zone updated successfully");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: "/zones" as any });
    } catch {
      toast.error("Failed to update zone");
    }
  };

  if (isLoading || step === "loading") {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-destructive mb-4">Failed to load zone</p>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Button onClick={() => navigate({ to: "/zones" as any })}>
          Back to Zones
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">

      <div className="flex items-center gap-4">
      <BreadcrumbNav
                items={[
                    { label: "Home", href: "/" },
                    { label: "Zones", href: "/zones" },
                    { label: zone?.name ?? "Zone" }
                ]}
                className="mb-6"
            />
        <Button 
          variant="outline" 
          size="icon" 
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => navigate({ to: "/zones" as any })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">Edit Zone: {zone?.name}</h1>
      </div>

      {step === "draw" ? (
        <Card>
          <CardHeader>
            <CardTitle>Edit Zone Shape</CardTitle>
            <CardDescription>
              Modify the zone on the map. You can drag points to adjust the shape.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 relative">
            <div className="h-[600px] w-full relative">
              <Map
                id="zone-map"
                mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                initialViewState={{
                  longitude: -0.1276,
                  latitude: 51.5072,
                  zoom: 12
                }}
                style={{ width: "100%", height: "100%" }}
                mapLib={import('maplibre-gl')}
                attributionControl={false}
                reuseMaps
              >
                <ZoneDraw 
                  onZoneDrawn={handleZoneDrawn} 
                  initialGeometry={zone?.geometry}
                />
              </Map>
            </div>
            <div className="p-4 flex justify-end">
              <Button 
                onClick={handleContinue}
                disabled={!zoneGeometry}
              >
                Continue to Zone Details
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Zone Details</CardTitle>
            <CardDescription>
              Update information about this zone
            </CardDescription>
          </CardHeader>
          <CardContent>
            {zone && zoneGeometry && (
              <ZoneForm 
                onSubmit={handleSubmit} 
                zoneGeometry={zoneGeometry}
                geometryType={zoneType || 'circle'}
                initialValues={{
                  name: zone.name,
                  description: zone.description,
                  category: zone.category,
                  color: zone.color
                }}
                isSubmitting={updateZoneMutation.isPending}
              />
            )}
            <div className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => setStep("draw")}
                className="mr-2"
              >
                Back to Drawing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 